import * as Sentry from '@sentry/react'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ClerkProvider, ClerkLoaded, ClerkLoading } from '@clerk/clerk-react'
import { Toaster } from 'sonner'
import ErrorFallback from '@/components/ErrorFallback'
import { clerkPublishableKey } from '@/lib/auth'
import { clerkAppearance } from '@/lib/clerkAppearance'
import { queryClient } from '@/lib/queryClient'
import { initAnalytics } from '@/lib/analytics'
import { registerServiceWorker } from '@/lib/registerServiceWorker'
import { listenForInstallPrompt } from '@/lib/installPrompt'
import { TooltipProvider } from '@/components/ui/tooltip'
import { ThemeProvider } from '@/hooks/useTheme'
import { CurrentHouseholdProvider } from '@/hooks/useCurrentHousehold'
import App from './App'
import './index.css'

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN

// Sentry no-ops when DSN is empty — safe to call unconditionally
Sentry.init({
  dsn: SENTRY_DSN || undefined,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
  release: import.meta.env.VITE_SENTRY_RELEASE,
  tracesSampleRate: 0.1,
  sendDefaultPii: false,
  beforeSend(event) {
    // Strip values of sensitive financial fields from event payloads
    const sensitiveFields = new Set([
      'balance',
      'amount',
      'account_number',
      'accountId',
      'transactionId',
    ])
    if (event.request?.data && typeof event.request.data === 'object') {
      for (const key of sensitiveFields) {
        if (key in (event.request.data as Record<string, unknown>)) {
          ;(event.request.data as Record<string, unknown>)[key] = '[Filtered]'
        }
      }
    }
    return event
  },
  beforeBreadcrumb(breadcrumb) {
    // Strip input values from UI breadcrumbs
    if (breadcrumb.category === 'ui.input' && breadcrumb.message) {
      breadcrumb.message = '[Filtered]'
    }
    return breadcrumb
  },
})

// No-ops (and loads nothing) when VITE_POSTHOG_KEY is empty; fire-and-forget otherwise.
void initAnalytics()

// Installs the PWA service worker (shell precache only, auto-updating).
registerServiceWorker()
listenForInstallPrompt()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      <ThemeProvider>
        <ClerkProvider publishableKey={clerkPublishableKey} appearance={clerkAppearance}>
          <QueryClientProvider client={queryClient}>
            {/* Nothing below fires an API request until Clerk has actually attached a session —
             *  querying earlier races window.Clerk?.session, which the axios interceptor in
             *  api/client.ts treats as a genuine logged-out 401 (it clears the stored household
             *  and signs out), even though Clerk is still mid-hydration on a hard refresh. */}
            <ClerkLoading>
              <div className="flex min-h-screen items-center justify-center">
                <p className="text-sm text-muted-foreground">Loading…</p>
              </div>
            </ClerkLoading>
            <ClerkLoaded>
              <CurrentHouseholdProvider>
                <TooltipProvider delayDuration={250} skipDelayDuration={300}>
                  <BrowserRouter>
                    <App />
                    <Toaster richColors position="top-right" />
                    <ReactQueryDevtools initialIsOpen={false} />
                  </BrowserRouter>
                </TooltipProvider>
              </CurrentHouseholdProvider>
            </ClerkLoaded>
          </QueryClientProvider>
        </ClerkProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>
)
